import { useEffect, useMemo, useRef, useState } from 'react';
import { MEANING_JOURNEY, type JourneyMeaning, type MapSnapshot } from '@smysly/shared';
import { sound } from '../lib/sound.js';
import { haptic } from '../lib/telegram.js';

const ROOT_LIMIT = 3;
const BRANCH_LIMIT = 2;

export function MeaningJourney({ snapshot, busy, onChoose }: { snapshot: MapSnapshot; busy: boolean; onChoose(titles: string[]): Promise<void> }): React.JSX.Element | null {
  const persisted = useMemo(() => new Set(snapshot.meanings.filter((meaning) => meaning.status !== 'popped').map((meaning) => meaning.title)), [snapshot.meanings]);
  const persistedRoots = MEANING_JOURNEY.filter((meaning) => persisted.has(meaning.title)).slice(0, ROOT_LIMIT);
  const [rootsCommitted, setRootsCommitted] = useState(persistedRoots.length >= ROOT_LIMIT);
  const [rootDraft, setRootDraft] = useState(() => new Set(persistedRoots.map((meaning) => meaning.title)));
  const firstOpen = persistedRoots.findIndex((meaning) => !meaning.branches.some((branch) => persisted.has(branch)));
  const [branchIndex, setBranchIndex] = useState(Math.max(0, firstOpen));
  const [branchDraft, setBranchDraft] = useState<Set<string>>(new Set());
  const [finished, setFinished] = useState(persistedRoots.length >= ROOT_LIMIT && firstOpen < 0);
  const [dismissed, setDismissed] = useState(persistedRoots.length >= ROOT_LIMIT && firstOpen < 0);
  const roots = MEANING_JOURNEY.filter((meaning) => persisted.has(meaning.title)).slice(0, ROOT_LIMIT);
  const focus = roots[branchIndex];

  useEffect(() => {
    if (!focus) return;
    setBranchDraft(new Set(focus.branches.filter((branch) => persisted.has(branch)).slice(0, BRANCH_LIMIT)));
  }, [focus?.title]);

  if (dismissed) return null;

  const toggleRoot = (title: string) => {
    setRootDraft((current) => toggle(current, title, ROOT_LIMIT));
  };
  const commitRoots = async () => {
    const additions = [...rootDraft].filter((title) => !persisted.has(title));
    if (rootDraft.size !== ROOT_LIMIT) return;
    await onChoose(additions);
    sound.play('journey_step');
    haptic('success', snapshot.preferences.haptics);
    setRootsCommitted(true);
  };
  const commitBranches = async () => {
    if (!focus || branchDraft.size < 1) return;
    await onChoose([...branchDraft].filter((title) => !persisted.has(title)));
    sound.play('journey_step');
    haptic('success', snapshot.preferences.haptics);
    if (branchIndex < roots.length - 1) setBranchIndex((index) => index + 1);
    else setFinished(true);
  };

  if (finished) return (
    <section className="journey journey--complete">
      <div className="journey__completion-orbit"><i /><i /><i /></div>
      <p className="eyebrow">Тропа проявилась</p>
      <h2>Ваши смыслы уже рядом</h2>
      <p>Двигайтесь по пространству. Цветные путники вокруг исследуют свои карты одновременно с вами.</p>
      <button className="button button--primary" onClick={() => setDismissed(true)}>Продолжить путешествие <span>→</span></button>
    </section>
  );

  if (!rootsCommitted) return (
    <section className="journey journey--cloud">
      <div className="journey__heading">
        <p className="eyebrow">Первая поляна</p>
        <h2>Облако смыслов</h2>
        <p>Коснитесь или удерживайте те, без которых ваша жизнь была бы не вашей.</p>
        <strong>{rootDraft.size} / {ROOT_LIMIT}</strong>
      </div>
      <div className="meaning-cloud">
        {MEANING_JOURNEY.map((meaning) => <HoldOrb key={meaning.title} meaning={meaning} selected={rootDraft.has(meaning.title)} onToggle={() => toggleRoot(meaning.title)} />)}
      </div>
      <button className="button button--primary journey__next" disabled={busy || rootDraft.size !== ROOT_LIMIT} onClick={() => void commitRoots()}>
        <span>{busy ? 'Смыслы собираются…' : 'Открыть свою тропу'}</span><span>→</span>
      </button>
    </section>
  );

  if (!focus) return null;
  return (
    <section className="journey journey--branch" style={{ '--meaning-color': focus.color } as React.CSSProperties}>
      <div className="journey__path"><i style={{ width: `${((branchIndex + 1) / roots.length) * 100}%` }} /></div>
      <p className="eyebrow">Шаг {branchIndex + 1} из {roots.length}</p>
      <div className="journey__root-symbol">{focus.symbol}</div>
      <h2>{focus.title}</h2>
      <p>Из чего именно для вас складывается этот смысл? Выберите один или два шара.</p>
      <div className="branch-cloud">
        {focus.branches.map((branch, index) => (
          <HoldOrb key={branch} meaning={{ ...focus, title: branch, symbol: String(index + 1), description: '' }} selected={branchDraft.has(branch)} onToggle={() => setBranchDraft((current) => toggle(current, branch, BRANCH_LIMIT))} compact />
        ))}
      </div>
      <button className="button button--primary journey__next" disabled={busy || branchDraft.size < 1} onClick={() => void commitBranches()}>
        <span>{busy ? 'Тропа меняется…' : branchIndex < roots.length - 1 ? 'Следующий смысл' : 'Увидеть свою карту'}</span><span>→</span>
      </button>
    </section>
  );
}

function HoldOrb({ meaning, selected, onToggle, compact = false }: { meaning: JourneyMeaning; selected: boolean; onToggle(): void; compact?: boolean }): React.JSX.Element {
  const timer = useRef<number | null>(null);
  const handledByHold = useRef(false);
  const [holding, setHolding] = useState(false);
  const cancel = () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
    setHolding(false);
  };
  const begin = () => {
    cancel();
    handledByHold.current = false;
    setHolding(true);
    timer.current = window.setTimeout(() => {
      timer.current = null;
      handledByHold.current = true;
      setHolding(false);
      sound.play(selected ? 'bubble_pop' : 'journey_select');
      haptic(selected ? 'pop' : 'create');
      onToggle();
    }, 460);
  };
  useEffect(() => cancel, []);
  return (
    <button
      className={`hold-orb${selected ? ' is-selected' : ''}${holding ? ' is-holding' : ''}${compact ? ' hold-orb--compact' : ''}`}
      style={{ '--orb-color': meaning.color } as React.CSSProperties}
      onPointerDown={begin}
      onPointerUp={cancel}
      onPointerCancel={cancel}
      onPointerLeave={cancel}
      onClick={() => {
        if (handledByHold.current) {
          handledByHold.current = false;
          return;
        }
        sound.play(selected ? 'bubble_pop' : 'journey_select');
        haptic(selected ? 'pop' : 'create');
        onToggle();
      }}
      aria-pressed={selected}
    >
      <i className="hold-orb__ring" />
      <span className="hold-orb__symbol">{selected ? '✓' : meaning.symbol}</span>
      <strong>{meaning.title}</strong>
      {!compact && <small>{meaning.description}</small>}
    </button>
  );
}

function toggle(current: Set<string>, title: string, limit: number): Set<string> {
  const next = new Set(current);
  if (next.has(title)) next.delete(title);
  else if (next.size < limit) next.add(title);
  return next;
}
